import { Button, Card, Col, DatePicker, Row, Spin, Typography } from "antd";
import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import useAxiosPost from "../../hooks/useAxiosPost";
import useMultiNotification from "../../hooks/useNotification";
import NetwalaSelect from "../Items/Select/Index";
import UsersIcon from "../../assets/svgs/MenuIcons/UsersIconBlue.svg";
import RechargeIcon from "../../assets/svgs/RechargeIconBlue.svg";
import ExpiryDateIcon from "../../assets/svgs/ExpiryDateIcon.svg";
import WarningIcon from "../../assets/svgs/WarningIcon.svg";
import "./DashboardReportsOverview.css";

const { RangePicker } = DatePicker;

const COLLECTION_TYPE_OPTIONS = [
  { label: "All", value: "all" },
  { label: "Recharged total amount", value: "recharge" },
  { label: "Inventory sale amount", value: "inventory_sale" },
  { label: "Other services total amount", value: "other_services" },
];

const PENDING_TYPE_OPTIONS = [
  { label: "All", value: "all" },
  { label: "Recharge pending balance", value: "recharge" },
  { label: "Inventory pending balance", value: "inventory_sale" },
  { label: "Other services pending", value: "other_services" },
];

const CARD_CONFIG = [
  /* No date range */
  {
    key: "totalCustomers",
    title: "Total Customers",
    icon: UsersIcon,
    gradient: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
    apiKey: "getTotalCustomers",
  },
  {
    key: "totalActiveConnections",
    title: "Total Active Connections",
    icon: UsersIcon,
    gradient: "linear-gradient(135deg, #059669 0%, #10b981 100%)",
    apiKey: "getTotalActiveConnections",
  },
  {
    key: "totalLost",
    title: "Lost (No Recharge 1 Month)",
    icon: WarningIcon,
    gradient: "linear-gradient(135deg, #dc2626 0%, #ef4444 100%)",
    apiKey: "getTotalLostConnections",
  },
  /* With date range */
  {
    key: "thisMonthRecharged",
    title: "Recharged (Period)",
    icon: RechargeIcon,
    gradient: "linear-gradient(135deg, #0284c7 0%, #0ea5e9 100%)",
    apiKey: "getThisMonthRecharged",
    hasFilter: true,
  },
  {
    key: "totalExpiring",
    title: "Expiring (Range)",
    icon: ExpiryDateIcon,
    gradient: "linear-gradient(135deg, #d97706 0%, #f59e0b 100%)",
    apiKey: "getTotalExpiringConnections",
    hasFilter: true,
  },
  {
    key: "totalExpired",
    title: "Expired (As of Date)",
    icon: ExpiryDateIcon,
    gradient: "linear-gradient(135deg, #475569 0%, #64748b 100%)",
    apiKey: "getTotalExpiredConnections",
    hasFilter: true,
  },
];

const DashboardReportsOverview = () => {
  const { openNotification, contextHolder } = useMultiNotification();
  const [reportsVisible, setReportsVisible] = useState(false);
  const [values, setValues] = useState<{
    totalCustomers: number | null;
    totalActiveConnections: number | null;
    thisMonthRecharged: number | null;
    totalExpiring: number | null;
    totalLost: number | null;
    totalExpired: number | null;
  }>({
    totalCustomers: null,
    totalActiveConnections: null,
    thisMonthRecharged: null,
    totalExpiring: null,
    totalLost: null,
    totalExpired: null,
  });

  const [rechargedRange, setRechargedRange] = useState<[Dayjs, Dayjs] | null>(
    () => [dayjs().startOf("month"), dayjs().endOf("month")]
  );
  const [expiringRange, setExpiringRange] = useState<[Dayjs, Dayjs] | null>(
    () => [dayjs().startOf("day"), dayjs().endOf("day")]
  );
  const [expiredAsOf, setExpiredAsOf] = useState<Dayjs | null>(() => dayjs());

  const [collectedDateRange, setCollectedDateRange] = useState<
    [Dayjs, Dayjs] | null
  >(() => [dayjs().startOf("month"), dayjs().endOf("month")]);
  const [collectedType, setCollectedType] = useState<string>("all");
  const [totalCollected, setTotalCollected] = useState<number | null>(null);

  const [pendingDateRange, setPendingDateRange] = useState<
    [Dayjs, Dayjs] | null
  >(() => [dayjs().startOf("month"), dayjs().endOf("month")]);
  const [pendingType, setPendingType] = useState<string>("all");
  const [totalPending, setTotalPending] = useState<number | null>(null);

  const [expenseDateRange, setExpenseDateRange] = useState<
    [Dayjs, Dayjs] | null
  >(() => [dayjs().startOf("month"), dayjs().endOf("month")]);
  const [expenseTag, setExpenseTag] = useState<string>("all");
  const [totalExpense, setTotalExpense] = useState<number | null>(null);
  const [expenseTagOptions, setExpenseTagOptions] = useState<
    { label: string; value: string }[]
  >([{ label: "All", value: "all" }]);

  const [receivableDateRange, setReceivableDateRange] = useState<
    [Dayjs, Dayjs] | null
  >(() => [dayjs().startOf("day"), dayjs().endOf("month")]);
  const [totalReceivable, setTotalReceivable] = useState<number | null>(null);

  const [
    getTotalCollectedAmount,
    resCollected,
    errCollected,
    loadingCollected,
  ] = useAxiosPost<ResponseProp>("reports/getTotalCollectedAmount");

  const [getTotalPendingAmount, resPending, errPending, loadingPending] =
    useAxiosPost<ResponseProp>("reports/getTotalPendingAmount");

  const [getTotalExpenseAmount, resExpense, errExpense, loadingExpense] =
    useAxiosPost<ResponseProp>("reports/getTotalExpenseAmount");

  const [getExpenseTags, resExpenseTags] = useAxiosPost<ResponseProp>(
    "transactions/getExpenseTags"
  );

  const [getTotalReceivable, resReceivable, errReceivable, loadingReceivable] =
    useAxiosPost<ResponseProp>("reports/getTotalReceivable");

  const [getTotalCustomers, resCustomers, errCustomers, loadingCustomers] =
    useAxiosPost<ResponseProp>("reports/getTotalCustomers");
  const [getTotalActive, resActive, errActive, loadingActive] =
    useAxiosPost<ResponseProp>("reports/getTotalActiveConnections");
  const [getRecharged, resRecharged, errRecharged, loadingRecharged] =
    useAxiosPost<ResponseProp>("reports/getThisMonthRecharged");
  const [getExpiring, resExpiring, errExpiring, loadingExpiring] =
    useAxiosPost<ResponseProp>("reports/getTotalExpiringConnections");
  const [getLost, resLost, errLost, loadingLost] = useAxiosPost<ResponseProp>(
    "reports/getTotalLostConnections"
  );
  const [getExpired, resExpired, errExpired, loadingExpired] =
    useAxiosPost<ResponseProp>("reports/getTotalExpiredConnections");

  const getResponseData = (
    r: ResponseProp | null
  ): Record<string, unknown> | undefined =>
    r?.responseData as Record<string, unknown> | undefined;

  useEffect(() => {
    getTotalCustomers({});
    getTotalActive({});
    getLost({});
  }, []);

  useEffect(() => {
    if (rechargedRange?.length === 2) {
      getRecharged({
        startDate: rechargedRange[0].format("YYYY-MM-DD"),
        endDate: rechargedRange[1].format("YYYY-MM-DD"),
      });
    }
  }, [rechargedRange]);

  useEffect(() => {
    if (expiringRange?.length === 2) {
      getExpiring({
        startDate: expiringRange[0].format("YYYY-MM-DD"),
        endDate: expiringRange[1].format("YYYY-MM-DD"),
      });
    }
  }, [expiringRange]);

  useEffect(() => {
    if (expiredAsOf) {
      getExpired({ asOfDate: expiredAsOf.format("YYYY-MM-DD") });
    }
  }, [expiredAsOf]);

  useEffect(() => {
    if (collectedDateRange?.length !== 2) return;
    getTotalCollectedAmount({
      startDate: collectedDateRange[0].format("YYYY-MM-DD"),
      endDate: collectedDateRange[1].format("YYYY-MM-DD"),
      collectionType: collectedType === "all" ? undefined : collectedType,
    });
  }, [collectedDateRange, collectedType]);

  useEffect(() => {
    if (pendingDateRange?.length !== 2) return;
    getTotalPendingAmount({
      startDate: pendingDateRange[0].format("YYYY-MM-DD"),
      endDate: pendingDateRange[1].format("YYYY-MM-DD"),
      pendingType: pendingType === "all" ? undefined : pendingType,
    });
  }, [pendingDateRange, pendingType]);

  useEffect(() => {
    getExpenseTags({});
  }, []);

  useEffect(() => {
    const data = resExpenseTags?.responseData as unknown;
    if (Array.isArray(data)) {
      const tags = (data as { tagName: string }[]).map((t) => ({
        label: t.tagName,
        value: t.tagName,
      }));
      setExpenseTagOptions([{ label: "All", value: "all" }, ...tags]);
    }
  }, [resExpenseTags]);

  useEffect(() => {
    if (expenseDateRange?.length !== 2) return;
    getTotalExpenseAmount({
      startDate: expenseDateRange[0].format("YYYY-MM-DD"),
      endDate: expenseDateRange[1].format("YYYY-MM-DD"),
      expenseTag: expenseTag === "all" ? undefined : expenseTag,
    });
  }, [expenseDateRange, expenseTag]);

  useEffect(() => {
    if (receivableDateRange?.length !== 2) return;
    getTotalReceivable({
      startDate: receivableDateRange[0].format("YYYY-MM-DD"),
      endDate: receivableDateRange[1].format("YYYY-MM-DD"),
    });
  }, [receivableDateRange]);

  useEffect(() => {
    const data = getResponseData(resCollected);
    if (data != null && typeof data.totalCollected === "number") {
      setTotalCollected(data.totalCollected as number);
    }
  }, [resCollected]);

  useEffect(() => {
    const data = getResponseData(resPending);
    if (data != null && typeof data.totalPending === "number") {
      setTotalPending(data.totalPending as number);
    }
  }, [resPending]);

  useEffect(() => {
    const data = getResponseData(resExpense);
    if (data != null && typeof data.totalExpense === "number") {
      setTotalExpense(data.totalExpense as number);
    }
  }, [resExpense]);

  useEffect(() => {
    const data = getResponseData(resReceivable);
    if (data != null && typeof data.totalReceivable === "number") {
      setTotalReceivable(data.totalReceivable as number);
    }
  }, [resReceivable]);

  useEffect(() => {
    const data = getResponseData(resCustomers);
    if (data != null && typeof data.totalCustomers === "number") {
      setValues((v) => ({
        ...v,
        totalCustomers: data.totalCustomers as number,
      }));
    }
  }, [resCustomers]);
  useEffect(() => {
    const data = getResponseData(resActive);
    if (data != null && typeof data.totalActiveConnections === "number") {
      setValues((v) => ({
        ...v,
        totalActiveConnections: data.totalActiveConnections as number,
      }));
    }
  }, [resActive]);
  useEffect(() => {
    const data = getResponseData(resRecharged);
    if (data != null && typeof data.totalRecharged === "number") {
      setValues((v) => ({
        ...v,
        thisMonthRecharged: data.totalRecharged as number,
      }));
    }
  }, [resRecharged]);
  useEffect(() => {
    const data = getResponseData(resExpiring);
    if (data != null && typeof data.totalExpiring === "number") {
      setValues((v) => ({ ...v, totalExpiring: data.totalExpiring as number }));
    }
  }, [resExpiring]);
  useEffect(() => {
    const data = getResponseData(resLost);
    if (data != null && typeof data.totalLost === "number") {
      setValues((v) => ({ ...v, totalLost: data.totalLost as number }));
    }
  }, [resLost]);
  useEffect(() => {
    const data = getResponseData(resExpired);
    if (data != null && typeof data.totalExpired === "number") {
      setValues((v) => ({ ...v, totalExpired: data.totalExpired as number }));
    }
  }, [resExpired]);

  const errs = [
    errCollected,
    errPending,
    errExpense,
    errReceivable,
    errCustomers,
    errActive,
    errRecharged,
    errExpiring,
    errLost,
    errExpired,
  ];
  useEffect(() => {
    const msg = errs.find(Boolean);
    if (msg) openNotification("error", "Report error", msg);
  }, [
    errCollected,
    errPending,
    errExpense,
    errReceivable,
    errCustomers,
    errActive,
    errRecharged,
    errExpiring,
    errLost,
    errExpired,
  ]);

  const getValue = (key: string): number | string => {
    const v = values[key as keyof typeof values];
    return v ?? "—";
  };

  const getLoadingForCard = (key: string): boolean => {
    switch (key) {
      case "totalCustomers":
        return loadingCustomers;
      case "totalActiveConnections":
        return loadingActive;
      case "thisMonthRecharged":
        return loadingRecharged;
      case "totalExpiring":
        return loadingExpiring;
      case "totalLost":
        return loadingLost;
      case "totalExpired":
        return loadingExpired;
      default:
        return false;
    }
  };

  const renderCardFilter = (config: (typeof CARD_CONFIG)[0]) => {
    if (config.key === "thisMonthRecharged") {
      return (
        <div className="report-card-filter">
          <RangePicker
            value={rechargedRange}
            onChange={(dates) =>
              setRechargedRange(
                dates && dates[0] && dates[1] ? [dates[0], dates[1]] : null
              )
            }
            format="DD MMM YY"
            size="small"
            className="report-card-date-picker"
          />
          {rechargedRange?.length === 2 && (
            <Typography.Text className="report-card-range-text">
              {rechargedRange[0].format("DD MMM")} –{" "}
              {rechargedRange[1].format("DD MMM YYYY")}
            </Typography.Text>
          )}
        </div>
      );
    }
    if (config.key === "totalExpiring") {
      return (
        <div className="report-card-filter">
          <RangePicker
            value={expiringRange}
            onChange={(dates) =>
              setExpiringRange(
                dates && dates[0] && dates[1] ? [dates[0], dates[1]] : null
              )
            }
            format="DD MMM YY"
            size="small"
            className="report-card-date-picker"
            disabledDate={(current) => {
              // Disable all dates before today
              return current && current.isBefore(dayjs().startOf("day"));
            }}
          />
          {expiringRange?.length === 2 && (
            <Typography.Text className="report-card-range-text">
              {expiringRange[0].format("DD MMM")} –{" "}
              {expiringRange[1].format("DD MMM YYYY")}
            </Typography.Text>
          )}
        </div>
      );
    }
    if (config.key === "totalExpired") {
      return (
        <div className="report-card-filter">
          <DatePicker
            value={expiredAsOf}
            onChange={(d) => setExpiredAsOf(d)}
            format="DD MMM YYYY"
            size="small"
            className="report-card-date-picker"
          />
          {expiredAsOf && (
            <Typography.Text className="report-card-range-text">
              As of {expiredAsOf.format("DD MMM YYYY")}
            </Typography.Text>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <>
      {contextHolder}
      <div className="dashboard-reports-overview">
        <div className="dashboard-reports-header">
          <Typography.Title level={4} className="dashboard-reports-title">
            Reports at a glance
          </Typography.Title>
          <Button
            type="primary"
            ghost
            icon={reportsVisible ? <EyeInvisibleOutlined /> : <EyeOutlined />}
            onClick={() => setReportsVisible((v) => !v)}
            className="reports-visibility-toggle"
          >
            {reportsVisible ? "Hide" : "Show"}
          </Button>
        </div>

        {!reportsVisible ? (
          <div className="reports-privacy-placeholder">
            <div className="reports-privacy-inner">
              <EyeInvisibleOutlined className="reports-privacy-icon" />
              <Typography.Text className="reports-privacy-text">
                Reports are hidden for privacy
              </Typography.Text>
              <Typography.Text className="reports-privacy-hint">
                Click Show above to view your reports
              </Typography.Text>
              <Button
                type="primary"
                icon={<EyeOutlined />}
                onClick={() => setReportsVisible(true)}
                size="large"
                className="reports-privacy-show-btn"
              >
                Show reports
              </Button>
            </div>
          </div>
        ) : (
          <>
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col xs={24} lg={12}>
                <Card className="report-collected-card-top">
                  <Spin spinning={loadingCollected} size="small">
                    <div className="report-collected-inner">
                      <div className="report-collected-content">
                        <Typography.Text className="report-collected-title">
                          Total Collected Amount
                        </Typography.Text>
                        <Typography.Text className="report-collected-value">
                          {totalCollected != null
                            ? Number(totalCollected).toLocaleString("en-US", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })
                            : "—"}
                        </Typography.Text>
                      </div>
                      <div className="report-collected-filters">
                        <div className="report-collected-filter-item">
                          <span className="report-collected-filter-label">
                            Date range
                          </span>
                          <RangePicker
                            value={collectedDateRange}
                            onChange={(dates) =>
                              setCollectedDateRange(
                                dates && dates[0] && dates[1]
                                  ? [dates[0], dates[1]]
                                  : null
                              )
                            }
                            format="DD MMM YY"
                            size="small"
                            className="report-collected-picker"
                          />
                        </div>
                        <div className="report-collected-filter-item">
                          <span className="report-collected-filter-label">
                            Type
                          </span>
                          <NetwalaSelect
                            value={collectedType}
                            onChange={(value) =>
                              setCollectedType(value as string)
                            }
                            options={COLLECTION_TYPE_OPTIONS}
                            size="small"
                            height="32px"
                            dropdownClassName="report-collected-type-dropdown"
                          />
                        </div>
                      </div>
                    </div>
                    {collectedDateRange?.length === 2 && (
                      <Typography.Text className="report-collected-range-hint">
                        {collectedDateRange[0].format("DD MMM YYYY")} –{" "}
                        {collectedDateRange[1].format("DD MMM YYYY")}
                        {collectedType !== "all" &&
                          ` · ${
                            COLLECTION_TYPE_OPTIONS.find(
                              (o) => o.value === collectedType
                            )?.label ?? collectedType
                          }`}
                      </Typography.Text>
                    )}
                  </Spin>
                </Card>
              </Col>
              <Col xs={24} lg={12}>
                <Card className="report-pending-card-top">
                  <Spin spinning={loadingPending} size="small">
                    <div className="report-collected-inner">
                      <div className="report-collected-content">
                        <Typography.Text className="report-collected-title">
                          Total Pending Amount (Customer)
                        </Typography.Text>
                        <Typography.Text className="report-collected-value">
                          {totalPending != null
                            ? Number(totalPending).toLocaleString("en-US", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })
                            : "—"}
                        </Typography.Text>
                      </div>
                      <div className="report-collected-filters">
                        <div className="report-collected-filter-item">
                          <span className="report-collected-filter-label">
                            Date range
                          </span>
                          <RangePicker
                            value={pendingDateRange}
                            onChange={(dates) =>
                              setPendingDateRange(
                                dates && dates[0] && dates[1]
                                  ? [dates[0], dates[1]]
                                  : null
                              )
                            }
                            format="DD MMM YY"
                            size="small"
                            className="report-collected-picker"
                          />
                        </div>
                        <div className="report-collected-filter-item">
                          <span className="report-collected-filter-label">
                            Type
                          </span>
                          <NetwalaSelect
                            value={pendingType}
                            onChange={(value) =>
                              setPendingType(value as string)
                            }
                            options={PENDING_TYPE_OPTIONS}
                            size="small"
                            height="32px"
                            dropdownClassName="report-collected-type-dropdown"
                          />
                        </div>
                      </div>
                    </div>
                    {pendingDateRange?.length === 2 && (
                      <Typography.Text className="report-collected-range-hint">
                        {pendingDateRange[0].format("DD MMM YYYY")} –{" "}
                        {pendingDateRange[1].format("DD MMM YYYY")}
                        {pendingType !== "all" &&
                          ` · ${
                            PENDING_TYPE_OPTIONS.find(
                              (o) => o.value === pendingType
                            )?.label ?? pendingType
                          }`}
                      </Typography.Text>
                    )}
                  </Spin>
                </Card>
              </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col xs={24}>
                <Card className="report-expense-card-top">
                  <Spin spinning={loadingExpense} size="small">
                    <div className="report-collected-inner">
                      <div className="report-collected-content">
                        <Typography.Text className="report-collected-title">
                          Total Expense
                        </Typography.Text>
                        <Typography.Text className="report-collected-value">
                          {totalExpense != null
                            ? Number(totalExpense).toLocaleString("en-US", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })
                            : "—"}
                        </Typography.Text>
                      </div>
                      <div className="report-collected-filters">
                        <div className="report-collected-filter-item">
                          <span className="report-collected-filter-label">
                            Date range
                          </span>
                          <RangePicker
                            value={expenseDateRange}
                            onChange={(dates) =>
                              setExpenseDateRange(
                                dates && dates[0] && dates[1]
                                  ? [dates[0], dates[1]]
                                  : null
                              )
                            }
                            format="DD MMM YY"
                            size="small"
                            className="report-collected-picker"
                          />
                        </div>
                        <div className="report-collected-filter-item">
                          <span className="report-collected-filter-label">
                            Expense tag
                          </span>
                          <NetwalaSelect
                            value={expenseTag}
                            onChange={(value) => setExpenseTag(value as string)}
                            options={expenseTagOptions}
                            size="small"
                            height="32px"
                            dropdownClassName="report-collected-type-dropdown"
                          />
                        </div>
                      </div>
                    </div>
                    {expenseDateRange?.length === 2 && (
                      <Typography.Text className="report-collected-range-hint">
                        {expenseDateRange[0].format("DD MMM YYYY")} –{" "}
                        {expenseDateRange[1].format("DD MMM YYYY")}
                        {expenseTag !== "all" &&
                          ` · ${
                            expenseTagOptions.find(
                              (o) => o.value === expenseTag
                            )?.label ?? expenseTag
                          }`}
                      </Typography.Text>
                    )}
                  </Spin>
                </Card>
              </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col xs={24}>
                <Card className="report-receivable-card-top">
                  <Spin spinning={loadingReceivable} size="small">
                    <div className="report-collected-inner">
                      <div className="report-collected-content">
                        <Typography.Text className="report-collected-title">
                          Total Receivable
                        </Typography.Text>
                        <Typography.Text className="report-receivable-subtitle">
                          Expected when connections expire in this period
                          (package + static IP - discount)
                        </Typography.Text>
                        <Typography.Text className="report-collected-value">
                          {totalReceivable != null
                            ? Number(totalReceivable).toLocaleString("en-US", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })
                            : "—"}
                        </Typography.Text>
                      </div>
                      <div className="report-collected-filters">
                        <div className="report-collected-filter-item">
                          <span className="report-collected-filter-label">
                            Date range (expiring in)
                          </span>
                          <RangePicker
                            value={receivableDateRange}
                            onChange={(dates) =>
                              setReceivableDateRange(
                                dates && dates[0] && dates[1]
                                  ? [dates[0], dates[1]]
                                  : null
                              )
                            }
                            format="DD MMM YY"
                            size="small"
                            className="report-collected-picker"
                            disabledDate={(current) => {
                              // Disable all dates before today
                              return (
                                current &&
                                current.isBefore(dayjs().startOf("day"))
                              );
                            }}
                          />
                        </div>
                      </div>
                    </div>
                    {receivableDateRange?.length === 2 && (
                      <Typography.Text className="report-collected-range-hint">
                        Connections expiring{" "}
                        {receivableDateRange[0].format("DD MMM YYYY")} –{" "}
                        {receivableDateRange[1].format("DD MMM YYYY")}
                      </Typography.Text>
                    )}
                  </Spin>
                </Card>
              </Col>
            </Row>

            <Row gutter={[16, 16]} className="dashboard-reports-cards">
              {CARD_CONFIG.map((config) => {
                const isLoading = getLoadingForCard(config.key);
                return (
                  <Col xs={24} sm={12} lg={8} xl={8} key={config.key}>
                    <Card
                      className="report-stat-card"
                      style={{ ["--card-gradient" as string]: config.gradient }}
                    >
                      <Spin spinning={isLoading} size="small">
                        <div className="report-stat-inner">
                          <div className="report-stat-icon-wrap">
                            <img
                              src={config.icon}
                              alt=""
                              className="report-stat-icon"
                            />
                          </div>
                          <div className="report-stat-content">
                            <Typography.Text className="report-stat-title">
                              {config.title}
                            </Typography.Text>
                            <Typography.Text className="report-stat-value">
                              {typeof getValue(config.key) === "number"
                                ? Number(getValue(config.key)).toLocaleString()
                                : getValue(config.key)}
                            </Typography.Text>
                          </div>
                        </div>
                        {config.hasFilter && (
                          <div className="report-card-filter-wrap">
                            {renderCardFilter(config)}
                          </div>
                        )}
                      </Spin>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          </>
        )}
      </div>
    </>
  );
};

export default DashboardReportsOverview;
